'use client'

// Your posters.
//
// Opens on top of whatever sent you here rather than replacing it, and
// stops short of the top so the drawer underneath stays visible: you are
// usually picking one for an event you are halfway through describing, and
// losing that form to look at a grid would be its own small betrayal.
//
// The count is the storage. Every saved poster is one image at the size the
// world hangs it, deleting one deletes the file, and the cap comes off the
// tier, so the number in the corner is the honest state of your shelf.

import { useCallback, useEffect, useState } from 'react'
import Link from 'next/link'
import useGameStore from '../../stores/gameStore'
import { authFetch, useAccount } from '../auth/useUser'
import { Drawer, Card, Pill } from './Drawer'
import { kawaii } from '../../lib/ui/theme'
import { FORMATS } from '../../lib/poster/render'

/**
 * Mounted once, at page level. Picking hands the poster to the store, so
 * whatever opened the shelf can read it without this needing to know who
 * that was, and without a second copy of the drawer existing.
 */
export function PostersDrawer() {
  const open = useGameStore((s) => s.postersOpen)
  const setOpen = useGameStore((s) => s.setPostersOpen)
  const onPick = useGameStore((s) => s.setPickedPoster)
  const { signedIn } = useAccount()

  const [posters, setPosters] = useState(null)
  const [allowed, setAllowed] = useState(0)
  const [busy, setBusy] = useState(null)
  const [note, setNote] = useState(null)
  const [broke, setBroke] = useState(null)

  // A failed read used to land in the same place as a successful empty one,
  // so a missing table, a dead database and a genuinely empty shelf all
  // rendered as "nothing here yet". That is the most expensive kind of
  // wrong: it looks fine, so nobody goes looking.
  const load = useCallback(() => {
    authFetch('/api/posters', { cache: 'no-store' })
      .then(async (r) => {
        const b = await r.json().catch(() => ({}))
        if (!r.ok) throw new Error(b.error || `the shelf did not load (${r.status})`)
        setPosters(Array.isArray(b.posters) ? b.posters : [])
        if (typeof b.allowed === 'number') setAllowed(b.allowed)
        setBroke(null)
      })
      .catch((err) => { setPosters([]); setBroke(err.message || 'the shelf did not load.') })
  }, [])

  // signedIn is in here on purpose: opening the shelf before the auth
  // island has answered would list nothing and look like an empty shelf.
  useEffect(() => { if (open) load() }, [open, load, signedIn])

  if (!open) return null

  const remove = async (id) => {
    setBusy(id); setNote(null)
    try {
      const res = await authFetch(`/api/posters?id=${encodeURIComponent(id)}`, { method: 'DELETE' })
      if (!res.ok) {
        const b = await res.json().catch(() => ({}))
        setNote(b.error || 'could not delete that one.')
      } else {
        setPosters((list) => (list || []).filter((p) => p.id !== id))
      }
    } catch {
      setNote('could not delete that one.')
    } finally {
      setBusy(null)
    }
  }

  const held = posters?.length ?? 0

  return (
    <Drawer
      level={1}
      title="Your posters"
      right={allowed ? <Pill bg={kawaii.creamDeep}>{held} of {allowed}</Pill> : null}
      subtitle="pick one to use it, or make another"
      onClose={() => setOpen(false)}
      height="min(58dvh, 560px)"
      bodyClass="kawaii-scroll flex-1 overflow-y-auto min-h-0 px-6 sm:px-10 pb-14 pt-1"
    >
      <div className="max-w-5xl mx-auto">
        {!signedIn && (
          <Card><p style={{ fontSize: 14, fontWeight: 700 }}>connect to keep posters.</p></Card>
        )}

        {signedIn && posters === null && (
          <p style={{ color: kawaii.inkSoft, fontSize: 14, fontWeight: 600, fontStyle: 'italic' }}>looking on the shelf…</p>
        )}

        {signedIn && broke && (
          <Card style={{ borderColor: '#a8483e' }}>
            <p style={{ fontSize: 14.5, fontWeight: 800, marginBottom: 4, color: '#a8483e' }}>
              could not read your shelf
            </p>
            <p style={{ color: kawaii.inkSoft, fontSize: 13, fontWeight: 600, lineHeight: 1.55 }}>
              {broke}
            </p>
          </Card>
        )}

        {signedIn && !broke && posters?.length === 0 && (
          <Card>
            <p style={{ fontSize: 14.5, fontWeight: 800, marginBottom: 4 }}>nothing on the shelf yet</p>
            <p style={{ color: kawaii.inkSoft, fontSize: 13.5, fontWeight: 600, lineHeight: 1.55, marginBottom: 10 }}>
              The poster maker turns a picture of yours into something that
              looks like it belongs on a wall in his town.
            </p>
            <MakeLink onGo={() => setOpen(false)} />
          </Card>
        )}

        {!!posters?.length && (
          <>
            <div className="grid gap-4" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))' }}>
              {posters.map((p) => (
                <div key={p.id}>
                  <button
                    type="button"
                    onClick={() => { onPick(p); setOpen(false) }}
                    title="use this one"
                    style={{
                      display: 'block', width: '100%', padding: 0,
                      border: kawaii.outlineThin, borderRadius: 12, overflow: 'hidden',
                      boxShadow: kawaii.dropSm, background: kawaii.creamDeep,
                      cursor: 'pointer', lineHeight: 0,
                    }}
                  >
                    {/* eslint-disable-next-line @next/next/no-img-element */}
                    <img
                      src={p.imageUrl}
                      alt={p.title || 'a poster'}
                      style={{
                        width: '100%', display: 'block',
                        aspectRatio: p.format === 'billboard' ? '2 / 1' : '2 / 3',
                        objectFit: 'cover',
                      }}
                    />
                  </button>
                  <div className="flex items-baseline justify-between gap-2" style={{ marginTop: 5 }}>
                    <span style={{ fontSize: 12.5, fontWeight: 800, color: kawaii.ink, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {p.title || 'untitled'}
                    </span>
                    <span style={{ color: kawaii.inkSoft, fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap' }}>
                      {FORMATS[p.format]?.label || p.format}
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() => remove(p.id)}
                    disabled={busy === p.id}
                    style={{
                      background: 'none', border: 'none', padding: 0, marginTop: 1,
                      color: '#a8483e', fontSize: 11.5, fontWeight: 800,
                      textDecoration: 'underline', cursor: 'pointer',
                    }}
                  >
                    {busy === p.id ? 'deleting…' : 'delete'}
                  </button>
                </div>
              ))}
            </div>

            <div style={{ marginTop: 16 }}>
              {held >= allowed ? (
                <p style={{ color: '#9a6a1e', fontSize: 12.5, fontWeight: 800 }}>
                  your shelf is full. delete one to make another.
                </p>
              ) : (
                <MakeLink onGo={() => setOpen(false)} />
              )}
            </div>
          </>
        )}

        {note && <p style={{ marginTop: 10, color: '#a8483e', fontSize: 12.5, fontWeight: 800 }}>{note}</p>}
      </div>
    </Drawer>
  )
}

function MakeLink({ onGo }) {
  return (
    <Link
      href="/poster"
      onClick={onGo}
      style={{
        display: 'inline-block', background: '#e8c04a', border: kawaii.outlineThin,
        borderRadius: 999, boxShadow: kawaii.dropSm, color: kawaii.ink,
        padding: '6px 16px', fontSize: 13, fontWeight: 800, textDecoration: 'none',
      }}
    >
      make a poster
    </Link>
  )
}

export default PostersDrawer
